import { useNavigate } from 'react-router-dom';
import Hero from '../components/Hero';
import Stats from '../components/Stats';

const Home = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: '⚖️',
      title: 'Evidence Integrity',
      description: 'We are honest about what science says and what it does not say. No overclaiming. No dismissal. Every modality assessed on its actual merit.'
    },
    {
      icon: '🌍',
      title: 'Globally Recognized',
      description: 'ICCAC credentials are recognized across 30+ countries, compatible with ICF and EMCC competency frameworks, and verifiable via our digital registry.'
    },
    {
      icon: '🛡️',
      title: 'Client Protection First',
      description: 'Every credentialed coach is trained in informed consent, contraindication awareness, and scope of practice, because clients deserve coaches they can genuinely trust.'
    },
    {
      icon: '🔬',
      title: 'Annual Evidence Review',
      description: 'Our standards evolve every year through a rigorous annual review cycle. If new research changes the evidence picture, our standards change too.'
    },
    {
      icon: '🧘',
      title: 'Wisdom Respected',
      description: 'Contemplative traditions are not dismissed for lacking clinical trials. They are placed in their appropriate evidence tier and honored with the depth they deserve.'
    },
    {
      icon: '📜',
      title: 'Transparent Process',
      description: 'Every accreditation decision is documented. Annual Review Reports are published publicly. Our standards are not a black box, they are a record of reasoned decisions.'
    }
  ];

  const tiers = [
    {
      symbol: '✦',
      tier: 'Tier 1',
      title: 'Gold Standard Evidence',
      description: 'Multiple peer-reviewed clinical studies and meta-analyses. Coaches use strong, positive claim language with confidence, because the evidence supports it.',
      examples: ['Mindfulness', 'Emotional Intelligence', 'Positive Psychology', 'CBT Coaching', 'Breathwork']
    },
    {
      symbol: '◈',
      tier: 'Tier 2',
      title: 'Emerging Evidence',
      description: 'Promising pilot studies and strong adjacent science. Coaches use appropriately qualified language that reflects the developing nature of the evidence.',
      examples: ['NLP Coaching', 'Manifestation Psychology', 'Sound Healing', 'Somatic Coaching']
    },
    {
      symbol: '◇',
      tier: 'Tier 3',
      title: 'Contemplative & Philosophical',
      description: 'Centuries of documented wisdom tradition. Not lesser. Honest. Coaches represent the philosophical basis to clients clearly, building deeper and more durable trust.',
      examples: ['Spiritual Coaching', 'Vedantic Self-Inquiry', 'Consciousness Coaching']
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <Hero
        title="Where Science Meets Ancient Wisdom"
        subtitle="The Global Standard for Consciousness-Based Coaching"
        ctaText="Apply for Accreditation →"
        onCtaClick={() => navigate('/accreditation')}
      />

      {/* Intro */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <p className="text-xl text-gray-700 max-w-4xl mx-auto">
          ICCAC is the world's only accreditation body that classifies every coaching modality by its actual evidence level, 
          holding coaches to honest, rigorous, internationally recognized standards.
        </p>
      </div>

      {/* Stats */}
      <Stats />

      {/* Why ICCAC */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1e3a5f] mb-4">Why ICCAC</h2>
            <p className="text-xl text-gray-600">The Credential That Means Something Different</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-lg hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-[#1e3a5f] mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Evidence Framework */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1e3a5f] mb-4">The Evidence Framework</h2>
            <p className="text-xl text-gray-600">Honest About Evidence. Rigorous About Standards.</p>
          </div>
          <div className="space-y-8">
            {tiers.map((tier, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-md">
                <div className="flex items-start">
                  <div className="text-4xl mr-4">{tier.symbol}</div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#1e3a5f] mb-2">{tier.tier}</h3>
                    <h4 className="text-xl font-semibold text-gray-800 mb-3">{tier.title}</h4>
                    <p className="text-gray-600 mb-4">{tier.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {tier.examples.map((example, i) => (
                        <span key={i} className="px-3 py-1 bg-blue-100 text-[#1e3a5f] rounded-full text-sm font-medium">
                          {example}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Three Pillars */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1e3a5f] mb-4">Three Pillars</h2>
            <p className="text-xl text-gray-600">What ICCAC Does</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="text-5xl font-bold text-[#1e3a5f] mb-4">I</div>
              <h3 className="text-2xl font-bold text-[#1e3a5f] mb-4">Accreditation</h3>
              <p className="text-gray-600">
                Independent assessment of coaching programs and individual coaches against internationally validated standards, 
                including a rigorous 10-criterion Evidence Audit.
              </p>
            </div>
            <div className="text-center p-8">
              <div className="text-5xl font-bold text-[#1e3a5f] mb-4">II</div>
              <h3 className="text-2xl font-bold text-[#1e3a5f] mb-4">Certification</h3>
              <p className="text-gray-600">
                World-class certification programs grounded in current research at the appropriate evidence tier, 
                designed for real-world coaching effectiveness.
              </p>
            </div>
            <div className="text-center p-8">
              <div className="text-5xl font-bold text-[#1e3a5f] mb-4">III</div>
              <h3 className="text-2xl font-bold text-[#1e3a5f] mb-4">Community</h3>
              <p className="text-gray-600">
                A global network of consciousness-forward coaches, researchers, and wisdom tradition practitioners 
                committed to elevating the profession.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-[#1e3a5f] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Talk About What's Next?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Learn more about our platforms and solutions
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="bg-white text-[#1e3a5f] px-8 py-3 rounded-md font-semibold hover:bg-gray-100 transition-colors text-lg"
          >
            Contact Us
          </button>
        </div>
      </div>
    </div>
  );
};

export default Home;